<template>
  <div>
    <Banner
      heading="School operational status"
      subtitle="Review and update school operational statuses."
    />
    <v-card
      v-if="loaded && !hasPermission"
      class="pa-6 ma-12"
    >
      <h3 class="mb-3">No Access</h3>
      <p>Sorry, you haven't been granted access to maintain school operational statuses.</p>
      <p>Please contact <b>{{ accessContact }}</b> if you believe you should have access.</p>
    </v-card>

    <v-stepper v-model="currentStep" v-if="hasPermission || !loaded">
      <v-stepper-header>
        <v-stepper-step
          :complete="currentStep > 1"
          step="1"
        >
          Select Schools
        </v-stepper-step>
        <v-divider />
        <v-stepper-step
          :complete="currentStep > 2"
          step="2"
        >
          Review and Confirm
        </v-stepper-step>
      </v-stepper-header>

      <v-stepper-items>
        <v-stepper-content step="1">
          <v-card class="pa-6 ma-1">
            <h3>School operational statuses</h3>
            <div class="ma-6">
              <v-text-field
                v-model="search"
                append-icon="search"
                label="Search..."
                single-line
                hint="Hint: You can search by school code, name, status, or a comma separated list of school codes."
                persistent-hint
                clearable
                @input="currentPage = 1;"
              />
            </div>
            <v-data-table
              :loading="!loaded"
              v-model="selected"
              :headers="tableHeaders"
              :items="schools"
              item-key="code"
              :options="options"
              :footer-props="footerProps"
              :search="search"
              :custom-filter="filterSchools"
              show-select
              :page.sync="currentPage"
            >
              <template v-slot:[`item.status`]="{item}">
                <Pill
                  :label="item.status"
                  :background="determinePillColor(item.status)"
                  color="#FFFFFF"
                  min-width="180px"
                />
              </template>
            </v-data-table>
          </v-card>
          <div class="mt-3 ml-1">
            <AdsButton
              @click="goToReview"
              :disabled="!selected || !selected.length"
            >
              <div class="d-flex align-center">
                <div>Review {{ selected.length }} selected</div>
                <v-icon class="ml-2">
                  mdi-arrow-right
                </v-icon>
              </div>
            </AdsButton>
          </div>
        </v-stepper-content>

        <v-stepper-content step="2">
          <AdsButton
            button-text="Back"
            icon="mdi-arrow-left"
            @click="currentStep = 1"
            tertiary
          />
          <v-card
            class="pa-6 mx-1 mt-3"
            elevation="1"
          >
            <div class="d-flex align-center justify-space-between mb-6">
              <h3>Selected schools ({{ selected.length }})</h3>
              <div class="ml-12">
                <AdsButton
                  :button-text="`Unselect all (${selected.length})`"
                  icon="mdi-cancel"
                  secondary
                  aria-label="Unselect all schools"
                  @click="unselectAllSchools"
                />
              </div>
            </div>
            <v-divider />
            <v-list class="pb-0 pt-0">
              <v-list-item>
                <v-list-item-content class="pa-0">
                  <v-row class="align-center">
                    <v-col cols="6" class="pb-1">
                      School
                    </v-col>
                    <v-col cols="3" class="pb-1">
                      Status
                    </v-col>
                    <v-col cols="12" md="3" class="pb-1"
                      :class="{ 'text-right': $vuetify.breakpoint.mdAndUp, 'pt-0': $vuetify.breakpoint.smAndDown }">
                      Operational
                    </v-col>
                  </v-row>
                </v-list-item-content>
                <v-list-item-action class="my-0">
                  <!-- alignment placeholder -->
                  <v-btn
                    icon
                    style="visibility: hidden"
                    height="0px"
                  >
                    <v-icon>mdi-cancel</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
            <v-divider />
            <v-list>
              <div
                v-for="(s, i) in selectedSorted"
                :key="i"
              >
              <v-list-item>
                <v-list-item-content class="pa-0">
                  <v-row class="align-center">
                    <v-col cols="6">
                      {{ s.code }} - <b>{{ s.name }}</b>
                    </v-col>
                    <v-col cols="3">
                      <Pill
                        :label="s.status"
                        :background="determinePillColor(s.status)"
                        color="#FFFFFF"
                        min-width="180px"
                      />
                    </v-col>
                    <v-col cols="12" md="3"
                      :class="{ 'text-right': $vuetify.breakpoint.mdAndUp, 'pt-0': $vuetify.breakpoint.smAndDown }"
                    >
                      {{ s.operationalDateFormatted }}
                    </v-col>
                  </v-row>
                </v-list-item-content>
                <v-list-item-action class="my-0">
                  <v-btn
                    icon
                    @click="unstage(s.code)"
                    :aria-label="`Unselect ${s.name}`"
                  >
                    <v-icon>mdi-cancel</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-divider v-if="$vuetify.breakpoint.smAndDown" />
              </div>
            </v-list>
          </v-card>
          <v-card
            class="px-6 pb-6 pt-1 mt-6 mx-1"
            elevation="1"
          >
            <v-radio-group
              class="school-closures--status-options"
              outline
              v-model="currentMode"
              label="Set school statuses to:"
            >
              <div class="mt-3">
                <v-radio
                  color="#00A908"
                  value="open"
                  label="Operational"
                >
                  <template v-slot:label>
                    <Pill
                      label="operational"
                      :background="determinePillColor('operational')"
                      color="#FFFFFF"
                      min-width="180px"
                    />
                  </template>
                </v-radio>
                <v-radio
                  color="#CE0037"
                  value="close"
                  label="Non-Operational"
                >
                  <template v-slot:label>
                    <Pill
                      label="non-operational"
                      :background="determinePillColor('non-operational')"
                      color="#FFFFFF"
                      min-width="180px"
                    />
                  </template>
                </v-radio>
              </div>
            </v-radio-group>
            <div
              class="mt-3"
              style="width: 290px"
            >
              <DatePicker
                v-model="effectiveDate"
                label="Effective on"
                hide-details
              />
            </div>
          </v-card>
          <div>
            <Alert
              in-page
              icon="mdi-alarm-light-outline"
              class="mt-3 mx-1"
              type="warning"
            >
              <template v-slot:content>
                <div>
                  <div>
                    Clicking <b>'Confirm'</b> below will send school status update notifications to subscribers of the selected school(s) in the NSW School Updates app.
                  </div>
                  <div>
                    <v-checkbox
                      v-model="disclaimerCheck"
                      label="I understand and wish to proceed"
                    />
                  </div>
                </div>
              </template>
            </Alert>
          </div>
          <div class="d-flex align-center justify-space-between mt-6 mx-1">
            <v-btn
              v-if="currentMode === 'open'"
              :disabled="!confirmEnabled"
              color="#00A908"
              @click="updateSchoolStatuses(false)"
              :dark="confirmEnabled"
            >
              <div class="d-flex align-center">
                <v-icon>mdi-door-open</v-icon>
                <span class="ml-3">Confirm</span>
              </div>
            </v-btn>

            <v-btn
              v-else-if="currentMode === 'close'"
              :disabled="!confirmEnabled"
              color="#CE0037"
              @click="updateSchoolStatuses(true)"
              :dark="confirmEnabled"
            >
              <div class="d-flex align-center">
                <v-icon>mdi-door-closed-lock</v-icon>
                <span class="ml-3">Confirm</span>
              </div>
            </v-btn>

            <v-btn
              v-else
              disabled
            >
              <div class="d-flex align-center">
                <v-icon>mdi-door-open</v-icon>
                <span class="ml-3">Confirm</span>
              </div>
            </v-btn>

            <AdsButton
              button-text="Cancel"
              @click="unselectAllSchools"
              tertiary
            />
          </div>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>

    <Snackbar
      v-model="showSuccess"
      type="success"
      :timeout="4000"
      text="Schools successfully updated"
    />
    <Snackbar
      v-model="showError"
      color="error"
      type="error"
      :text="errorMessage"
    >
      <v-btn
        dark
        text
        @click="showError = false"
      >
        Close
      </v-btn>
    </Snackbar>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { AdsButton, Banner, Alert } from '@nswdoe/doe-ui-core';
import Pill from '@/components/Pill';
import DatePicker from '@/components/DatePicker';
import Snackbar from '@/components/Snackbar';
import { PERMISSIONS } from '@/static/data';
import { getConfig } from '@/utils/config';

export default {
  name: 'SchoolStatuses',
  components: {
    Alert,
    AdsButton,
    Banner,
    Pill,
    DatePicker,
    Snackbar
  },
  data() {
    const defaultDate = new Date();

    return {
      loaded: false,
      currentStep: 1,
      currentMode: '',
      currentPage: 1,
      effectiveDate: defaultDate.toISOString(),
      disclaimerCheck: false,
      tableHeaders: [
        { text: 'Code', value: 'code', align: 'start', width: '100px'},
        { text: 'School', value: 'name', align: 'start'},
        { text: 'Status', value: 'status'},
        { text: 'Operational', value: 'operationalDateFormatted', align: 'end'},
        { text: 'Last Updated', value: 'lastUpdatedFormatted', align: 'end'}
      ],
      options: {
        itemsPerPage: 10,
        sortBy: ['name'],
        multiSort: false,
        mustSort: true
      },
      footerProps: {
        itemsPerPageOptions: [5, 10, 20, 50, 100, -1],
        showFirstLastPage: true,
        showCurrentPage: true,
        itemsPerPageAllText: "All",
      },
      search: '',
      selected: [],
      staged: [],

      showSuccess: false,
      showError: false,
      errorMessage: 'An error occurred.',
      accessContact: getConfig('VUE_APP_NSW_SCHOOL_SAFETY_ACCESS_ADMIN_EMAIL')
    }
  },
  computed: {
    ...mapGetters({
      schools: 'schools',
      permissions: 'permissions'
    }),
    confirmEnabled() {
      return this.selected && this.selected.length > 0 && this.disclaimerCheck;
    },
    selectedSorted() {
      return [...this.selected].sort((s1, s2) => (s1.name < s2.name) ? -1 : 1);
    },
    hasPermission() {
      return this.permissions && this.permissions.indexOf(PERMISSIONS.SCHOOL_STATUS_UPDATE) !== -1;
    }
  },
  async created() {
    await this.$store.dispatch('fetchPermissions');
    if (this.hasPermission) {
      const promises = []

      if (!this.schools || !this.schools.length) {
        promises.push(this.$store.dispatch('fetchSchools'));
      }

      await Promise.all(promises)
        .catch((err) => {
          this.loaded = true;
          this.errorMessage = "An error occurred. " + err;
          this.showError = true;
        });
    }
    this.loaded = true;
  },
  methods: {
    determinePillColor(s) {
      const colorMap = {
        'operational': '#00A908',
        'non-operational': '#CE0037'
      }
      return colorMap[s] || '#4C4F55';
    },
    filterSchools(value, search, school) {
      let csvs = search.split(',').map(s => s.toLowerCase().trim());
      csvs = csvs.filter(s => s != null && s !== '');
      return csvs.some(searchString => school.code.includes(searchString) || school.name?.toLowerCase().includes(searchString.toLowerCase()) || school.status.toLowerCase().includes(searchString.toLowerCase()))
    },
    unstage(schoolCode) {
      // the list and the selected list may be in a different order, so find the index by code, then remove it.
      const codes = this.selected.map(s => s.code);
      const idx = codes.indexOf(schoolCode);
      this.selected.splice(idx, 1);
    },
    goToReview() {
      this.currentMode = '';
      this.disclaimerCheck = false;
      this.currentStep = 2;
    },
    unselectAllSchools() {
      this.selected = [];
      this.currentStep = 1;
      this.currentMode = '';
      this.disclaimerCheck = false;
    },
    updateSchoolStatuses(nonOperational) {
      const schoolCodes = this.selected.map(s => s.code);
      this.$store.dispatch('updateSchoolStatuses', {schoolCodes, date: this.effectiveDate, nonOperational})
        .then(() => {
          this.loaded = false;
          this.$store.dispatch('fetchSchools')
            .then(() => {
              this.loaded = true;
            });
          this.unselectAllSchools();
          this.showSuccess = true;
        })
        .catch((err) => {
          console.error(err); // eslint-disable-line
          this.errorMessage = "An error occurred: " + err;
          this.showError = true;
        });
    }
  }
}
</script>

<style scoped lang="scss">
  ::v-deep .v-input {
    legend, label {
      color: $ads-dark;
    }
  }
  ::v-deep .v-input--radio-group__input .v-radio label.v-label {
    max-width: fit-content;
  }
</style>
